import { onBeforeUnmount, watch } from "vue";
import type { EventContext, EventType } from "@/types";
import { useDeviceState } from "../DeviceState";
import { storeToRefs } from "pinia";
import {
  enqueueEvent,
  flushEventQueue,
  persistEventQueue,
} from "@/analytics/event-queue";

/**
 * Record a product event.
 *
 * Fire-and-forget: the event is buffered and sent in a batch by
 * `@/analytics/event-queue`, which owns retries, offline storage and delivery.
 * Callers get no promise back on purpose — analytics must never make a UI
 * interaction wait, and must never surface an error to the user.
 */
export const useCreateEventLog = () => {
  const createEvent = (
    type: EventType,
    context?: EventContext,
    date?: Date,
  ) => {
    enqueueEvent(type, context, date);
  };

  return { createEvent };
};

/**
 * Drives delivery of the buffered events. Mount once, high in the tree.
 *
 * Flushes on three signals: coming back online, the page being hidden (mobile
 * backgrounds the app without ever firing `beforeunload`), and page unload.
 * The last two are best-effort — an interrupted request loses nothing, since
 * events only leave the buffer once the server has acknowledged them.
 */
export const useEventsController = () => {
  const deviceState = useDeviceState();
  const { isOnline } = storeToRefs(deviceState);

  watch(
    isOnline,
    (online) => {
      if (online) void flushEventQueue();
    },
    { immediate: true },
  );

  // Persist first, send second. Buffer writes are coalesced, so the last second
  // of activity may not be on disk yet — and the send is the part allowed to be
  // interrupted, since unacknowledged events survive in localStorage.
  const flushOnHide = () => {
    if (document.visibilityState !== "hidden") return;
    persistEventQueue();
    void flushEventQueue();
  };
  const flushOnUnload = () => {
    persistEventQueue();
    void flushEventQueue();
  };

  document.addEventListener("visibilitychange", flushOnHide);
  window.addEventListener("pagehide", flushOnUnload);

  onBeforeUnmount(() => {
    document.removeEventListener("visibilitychange", flushOnHide);
    window.removeEventListener("pagehide", flushOnUnload);
  });
};
